
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getMapCompanyBySlug } from 'services/company-service';
import { useSelector } from 'react-redux';
import { useAuthTenant, useAuthUser } from 'store/auth';
import { formatDateToDateBRPolish } from 'utils/date_utils';
import { getPendingPolicies } from 'services/policies-service';
import React from 'react';
import { Link, Box, Typography, Paper, Grid, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getComplianceProgramProgress } from '../../services/complice-program';
import { statusControl } from '../../utils/labels_utils';
import { PageHelper } from '../../Components/PageHelper';
import ReactECharts from 'echarts-for-react';
import { getPublicSiteUrl } from '../../utils/site_utils';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  Assessment,
  Security,
  DataUsage,
  Business,
  CheckCircle,
  Warning,
  Error,
  Schedule
} from '@mui/icons-material';


export const Dashboard = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const user = useSelector(useAuthUser);
  const selectedTenant = useSelector(useAuthTenant);
  const companyMap = useQuery(['getMapCompanyBySlug'], async () => {
    if (selectedTenant.slug === undefined) return null;
    const response = await getMapCompanyBySlug(selectedTenant.slug);
    return response?.data || null;
  });

  const pendingPolicies = useQuery(['getPendingPolicies'], async () => {
    if (selectedTenant.slug === undefined) return [];
    const response = await getPendingPolicies(selectedTenant.slug, user.userId);
    return response?.data || null;
  });
  
  const complianceCheck = useQuery(
    ['getComplianceProgramProgress', selectedTenant._id],
    async () => {
      if (!selectedTenant._id) return null;
      const response = await getComplianceProgramProgress(selectedTenant._id);
      return response?.data || null;
    },
    {
      enabled: !!selectedTenant._id,
    }
  );

  // Configuração do gráfico de pizza para status de controles
  const pieChartOption = {
    title: {
      text: t('dashboard:overview.controlsTitle'),
      left: 'center',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#334155'
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      orient: 'vertical',
      left: 'left',
      bottom: 20,
      textStyle: {
        fontSize: 12
      }
    },
    series: [
      {
        name: 'Status',
        type: 'pie',
        radius: ['40%', '70%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 8,
          borderColor: '#fff',
          borderWidth: 2
        },
        label: {
          show: false,
          position: 'center'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 20,
            fontWeight: 'bold'
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          }
        },
        labelLine: {
          show: false
        },
        data: Object.keys(complianceCheck.data?.statusCounts ?? {}).map((key) => ({
          name: statusControl[key]?.label || key,
          value: complianceCheck.data?.statusCounts[key],
          itemStyle: {
            color: statusControl[key]?.color || '#8884d8'
          }
        }))
      }
    ]
  };



  // Dados dos cards com ícones
  const cardData = [
    {
      title: t('dashboard:overview.areasCount'),
      value: companyMap?.data?.areas || 0,
      icon: <Business />,
      gradient: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      color: '#3B82F6'
    },
    {
      title: t('dashboard:overview.processesCount'),
      value: companyMap?.data?.processes || 0,
      icon: <Assessment />,
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
      color: '#8B5CF6'
    },
    {
      title: t('dashboard:overview.systemsCount'),
      value: companyMap?.data?.systems || 0,
      icon: <Security />,
      gradient: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
      color: '#F59E0B'
    },
    {
      title: t('dashboard:overview.assetsCount'),
      value: companyMap?.data?.assets || 0,
      icon: <DataUsage />,
      gradient: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
      color: '#10B981'
    },
    {
      title: t('dashboard:overview.datasCount'),
      value: companyMap?.data?.datas || 0,
      icon: <TrendingUp />,
      gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
      color: '#EC4899'
    }
  ];

  // Configuração dos passos do tour interativo
  const tourSteps = [
    {
      target: 'body',
      content: 'Bem-vindo à Visão Geral! Vamos fazer um tour rápido pelas principais funcionalidades desta página. Clique em "Próximo" para começar!',
      placement: 'center',
      disableBeacon: true,
    },
    {
      target: '[data-tour="company-header"]',
      content: 'Aqui é exibido o nome da sua empresa e a data atual. Este cabeçalho está presente em todas as páginas.',
      placement: 'bottom',
      title: '🏢 Informações da Empresa',
    },
    {
      target: '[data-tour="metrics-cards"]',
      content: 'Estes cards mostram quantos elementos você já mapeou no sistema: áreas, processos, sistemas, ativos e dados. Clique em qualquer card para navegar para a respectiva seção!',
      placement: 'bottom',
      title: '📊 Métricas de Mapeamento',
    },
    {
      target: '[data-tour="pending-policies"]',
      content: 'Aqui você encontra todas as políticas que precisam da sua aceitação. Clique em qualquer política para visualizá-la e aceitar os termos.',
      placement: 'top',
      title: '📋 Políticas Pendentes',
    },
    {
      target: '[data-tour="controls-status"]',
      content: 'Este gráfico mostra a distribuição dos status dos controles do seu Programa de Privacidade. Acompanhe o progresso da implementação visualmente!',
      placement: 'top',
      title: '🎯 Status dos Controles',
    },
    {
      target: 'body',
      content: 'Agora você conhece as principais áreas da Visão Geral. Você pode reativar este tour a qualquer momento clicando no botão de ajuda (?) no canto inferior direito.',
      placement: 'center',
      title: '✨ Tour Concluído!',
    },
  ];

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc' }}>
      {/* Helper Interativo */}
      <PageHelper 
        steps={tourSteps}
        tourKey="dashboard-tour"
        autoStart={true}
        showButton={true}
      />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {selectedTenant && (
          <Paper
            data-tour="company-header"
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 3,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {selectedTenant.name}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {formatDateToDateBRPolish(new Date())}
              </Typography>
            </Box>
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                right: -50,
                width: 200,
                height: 200,
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
                zIndex: 1
              }}
            />
          </Paper>
        )}
      </motion.div>

      {companyMap.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 8 }}>
          <CircularProgress size={60} />
        </Box>
      )}
      {!companyMap.error && !companyMap.isLoading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Grid container spacing={3} sx={{ mb: 4 }} data-tour="metrics-cards">
            {cardData.map((card, index) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                >
                  <Paper
                    elevation={0}
                    onClick={() => {
                      if (index === 0) {
                        navigate('/areas');
                      } else if (index === 1) {
                        navigate('/processes');
                      } else if (index === 2) {
                        navigate('/systems');
                      } else if (index === 3) {
                        navigate('/assets');
                      } else if (index === 4) {
                        navigate('/data');
                      }
                    }}
                    sx={{
                      p: 3,
                      background: card.gradient,
                      color: 'white',
                      borderRadius: 3,
                      position: 'relative',
                      overflow: 'hidden',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          p: 1.5,
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {card.icon}
                      </Box>
                      <Typography variant="h3" sx={{ fontWeight: 700, ml: 2 }}>
                        {card.value}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {card.title}
                    </Typography>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 80,
                        height: 80,
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '50%'
                      }}
                    />
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      )}


      {/* Seção de Políticas e Status */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
      >
        <Grid container spacing={3}>
          {/* Políticas Pendentes */}
          <Grid item xs={12} lg={6}>
            <Paper
              data-tour="pending-policies"
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                height: '400px',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Schedule sx={{ mr: 2, color: '#3B82F6' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('dashboard:overview.pendingPolicies')}
                </Typography>
              </Box>
              
              {pendingPolicies.isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {pendingPolicies.isSuccess && !pendingPolicies.isLoading && (
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                  {pendingPolicies.data?.length === 0 ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: 'text.secondary'
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 48, mb: 2, color: '#10B981' }} />
                      <Typography variant="body1">
                        {t('dashboard:overview.noPendingPolicies')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {pendingPolicies.data?.map((pendingPolicy, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          whileHover={{ scale: 1.02 }}
                        >
                          <Box
                            onClick={() => {
                              window.open(
                                getPublicSiteUrl(selectedTenant.slug, `/accept-policy-version/${pendingPolicy._id}`),
                                '_blank',
                              );
                            }}
                            sx={{
                              p: 2.5,
                              backgroundColor: '#f8fafc',
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: '#3B82F6',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Warning sx={{ mr: 2, color: '#F59E0B' }} />
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {pendingPolicy.policyName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {t('dashboard:overview.policyVersion')} {pendingPolicy.policyVersion}
                                </Typography>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                p: 1,
                                backgroundColor: '#3B82F6',
                                borderRadius: 1,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"/>
                              </svg>
                            </Box>
                          </Box>
                        </motion.div>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Status de Controles */}
          <Grid item xs={12} lg={6}>
            <Paper
              data-tour="controls-status"
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                height: '400px'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Assessment sx={{ mr: 2, color: '#8B5CF6' }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('dashboard:overview.controlsStatusTitle')}
                </Typography>
              </Box>
              
              {companyMap.isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              )}
              {companyMap.isSuccess && !companyMap.isLoading && (
                complianceCheck.data?.hasComplianceProgram === false ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '300px',
                      textAlign: 'center'
                    }}
                  >
                    <Error sx={{ fontSize: 48, mb: 2, color: '#EF4444' }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      {t('dashboard:overview.programNotFound')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      {t('dashboard:overview.noProgramDesc')}
                    </Typography>
                    <Link
                      href="/compliance-program/add"
                      sx={{
                        px: 3,
                        py: 1.5,
                        backgroundColor: '#3B82F6',
                        color: 'white',
                        borderRadius: 2,
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#2563EB'
                        }
                      }}
                    >
                      {t('dashboard:overview.registerProgram')}
                    </Link>
                  </Box>
                ) : (
                  <ReactECharts
                    option={pieChartOption}
                    style={{ height: '300px' }}
                    opts={{ renderer: 'svg' }}
                  />
                )
              )}
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
};
